import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Tooltip,
  Typography,
  Breadcrumb,
  Table,
  Segmented,
} from "antd";
import {
  BorderlessTableOutlined,
  BookOutlined,
  TableOutlined,
  ContainerOutlined,
} from "@ant-design/icons";
import get from "lodash/get";
import find from "lodash/find";
import map from "lodash/map";
import dayjs from "dayjs";
import requireAuth from "../../hocs/requireAuth";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { fetchPnlReport } from "../../redux/actions/reportActions";
import { fetchPnlExcelreport } from "../../redux/actions/downloadPdfAction";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import { COMPANY_ADMIN_ROLE_ACCESS, PNL_PERMISSION } from "../../constants";
import PnlTableSummary from "../../components/reports/pnlSummaryReport/pnlTableSummary";
import HeaderSelector from "../../components/reports/pnlSummaryReport/headerSelector";
import { pnlManagerShiftMetaColumns, pnlSummaryColumns } from "../../constants/tableColumns/summaryReportsColumns/getPnlSummaryColumns";
import PnlOverallSummary from "../../components/reports/pnlSummaryReport/pnlOverallSummary";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs, ORGANISATION_SUPERVISORS_IDs } from "../../constants/userRoles";


const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <BookOutlined style={{ fontSize: "16px" }} />
              <span>PNL</span>
            </>
          ),
        },
        {
          title: "Summary Report",
        },
      ]}
    />
  );
};

const PnlManagerWiseReportsScreen = () => {
  const [managerSummary, setManagerSummary] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState("list");
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);
  const [form] = Form.useForm();
  const [filter, setFilters] = useState({
    company: undefined,
    templateType: undefined,
    date: dayjs(),
  });

  const setinitialCompany = (company) => {
    const hasPermission = find(
      get(company, "feature_permissions", []),
      ['key', PNL_PERMISSION]
    );

    if (!hasPermission) return;
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });

    setConfig({
      ...config,
      companies: [company],
      templates: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getSummary = (query) => {
    setLoading(true);
    query = query
      ? query
      : {
        type: get(filter, "templateType"),
        company_uuid: get(filter, "company.id"),
        date: get(filter, "date").format("YYYY-MM-DD"),
      };
    dispatch(fetchPnlReport(query))
      .then((result) => {
        setManagerSummary(result);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadExcelReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      date: get(filter, "date").format("YYYY-MM-DD"),
    };
    dispatch(fetchPnlExcelreport(query, 'PNL'))
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies()
  }, [])

  return (
    <div className="pl-2 py-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>
      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto  pb-5 min-w-full">
        <Spin spinning={loading}>
          <Form form={form}>
            <div className="w-fit px-8 py-6">
              <HeaderSelector
                filter={filter}
                config={config}
                form={form}
                setManagerSummary={setManagerSummary}
                setFilters={setFilters}
                setConfig={setConfig}
                getSummary={() =>
                  getSummary({
                    type: get(filter, "templateType"),
                    company_uuid: get(filter, "company.id"),
                    date: get(filter, "date").format("YYYY-MM-DD"),
                  })
                }
              />
            </div>
          </Form>
          {get(get(managerSummary, "stats"), "length") > 0 && (
            <div>
              <div className="w-full px-8 overflow-y-auto">
                <div className="flex justify-end items-end">

                  <div className="flex justify-between gap-4 items-center w-full">
                    <Segmented
                      shape="round"
                      value={selectedSegment}
                      className={`bg-primary`}
                      options={[
                        {
                          value: "list",
                          icon: (
                            <Tooltip title="PNL Manager Wise Summary List">

                              <TableOutlined
                                style={{
                                  color: selectedSegment === "summary" ? "white" : "",
                                }}
                              />
                            </Tooltip>

                          ),
                        },
                        {
                          value: "summary",
                          icon: (
                            <Tooltip title="PNL Managers Total Summary">
                              <ContainerOutlined
                                style={{
                                  color: selectedSegment === "list" ? "white" : "",
                                }}
                              />
                            </Tooltip>
                          ),
                        },
                      ]}
                      onChange={(value) => setSelectedSegment(value)}
                    />
                    <Tooltip title="Export Summary Report Excel">
                      <ExcelExportButton
                        onDownload={onDownloadExcelReport}
                      />
                    </Tooltip>
                  </div>
                </div>
                <div>
                  {selectedSegment === "list" && (
                    <div>
                      {map(get(managerSummary, "stats"), (summary, index) => (
                        <div className="w-full table-container" key={index}>
                          <Title className="mt-10" level={5}>
                            {get(summary, "name")}
                          </Title>
                          <Table
                            className="mb-20 custom-horizontal-scrollbar"
                            dataSource={get(summary, "shifts")}
                            columns={pnlSummaryColumns}
                            bordered
                            size="small"
                            pagination={false}
                            rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                            summary={() => <PnlTableSummary summary={summary} templateType={get(filter, 'templateType')} />}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedSegment === "summary" && (
                    <div className="w-full table-container">
                      <Title className="mt-10" level={5}>
                        Summary
                      </Title>
                      <Table
                        className="mb-20 custom-horizontal-scrollbar"
                        dataSource={get(managerSummary, "stats")}
                        columns={pnlManagerShiftMetaColumns}
                        bordered
                        size="small"
                        pagination={false}
                        rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                        summary={() => <PnlOverallSummary summary={managerSummary} templateType={get(filter, 'templateType')} />}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default requireAuth(PnlManagerWiseReportsScreen, [
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs,
  ...ORGANISATION_SUPERVISORS_IDs
], PNL_PERMISSION);
